import { defineStore } from 'pinia'
import AuthService from '@/services/AuthService'
import localPermissions from '@/constants/localPermissions'

const toAuthCollaborator = ({ technical_id, firstname, lastname }) => ({
  technical_id,
  firstname,
  lastname
})

const persistAuthCollaborators = (store, collaborators) => {
  store.userDetail.collaborators = collaborators

  if (store.currentCollaborator?.technical_id) {
    const currentCollaborator = collaborators.find(
      ({ technical_id }) => technical_id === store.currentCollaborator.technical_id
    )
    store.currentCollaborator = currentCollaborator || null
    if (currentCollaborator) {
      localStorage.setItem('currentCollaborator', JSON.stringify(currentCollaborator))
    } else {
      localStorage.removeItem('currentCollaborator')
    }
  }

  localStorage.setItem('userDetail', JSON.stringify(store.userDetail))
}

export const useAuthStore = defineStore('auth', {
  state: () => {
    const currentCollaborator = JSON.parse(localStorage.getItem('currentCollaborator'))
    const filtersHistoryLocal = JSON.parse(localStorage.getItem('filtersHistory'))
    const userDetailLocalStorage = JSON.parse(localStorage.getItem('userDetail'))
    return {
      auth: currentCollaborator ? { status: { loggedIn: true } } : { status: { loggedIn: false } },
      userDetail: userDetailLocalStorage ? userDetailLocalStorage : {},
      currentCollaborator: currentCollaborator,
      filtersHistory: filtersHistoryLocal
        ? filtersHistoryLocal
        : { repairFileList: { status: false } },
      repairFile: { StoreSelected: null }
    }
  },
  getters: {
    hasPermission: (state) => (permissionsRequired) => {
      if (!state.userDetail || !state.userDetail.permissions) {
        return false
      }
      const permissionSet = new Set(state.userDetail.permissions.map((p) => p.title))
      return permissionsRequired.some(
        (permission) =>
          localPermissions[permission] && permissionSet.has(localPermissions[permission].title)
      )
    },
    hasSeveralCollaborators(state) {
      return (state.userDetail && state.userDetail.has_several_collaborators) || false
    },
    getCurrentCollaborator(state) {
      return state.currentCollaborator
    },
    getCurrentUser(state) {
      return state.userDetail || null
    },
    getCollaboratorList(state) {
      if (!state.userDetail || !state.userDetail.collaborators) {
        return []
      }
      return state.userDetail.collaborators
    },
    getNetworkLogo(state) {
      return (state.userDetail && state.userDetail.network?.logo) || null
    },
    getStoreListCollaborator(state) {
      return (state.userDetail && state.userDetail.store_list) || []
    },
    getRepairFileStoreSelected(state) {
      return state.repairFile.StoreSelected
    },
    getRepairFileListFilters(state) {
      return state.filtersHistory.repairFileList
    }
  },
  actions: {
    async register(userData) {
      return await AuthService.register(userData)
    },
    async loginValidation(userData) {
      try {
        return await AuthService.loginValidation(userData)
      } catch (error) {
        this.logout()
        throw error
      }
    },
    async loginFinalization(userData) {
      try {
        await AuthService.loginFinalization(userData)
        await this.me()
        this.loginSuccess()
      } catch (error) {
        this.logout()
        throw error
      }
    },
    async verifyLogin2faCode(code) {
      await AuthService.verifyLogin2faCode(code)
    },
    async resendLogin2faCode() {
      await AuthService.resendLogin2faCode()
    },
    async me() {
      const connectedUserDetail = await AuthService.me()
      this.userDetail = connectedUserDetail
      localStorage.setItem('userDetail', JSON.stringify(connectedUserDetail))
    },
    async logoutAction() {
      try {
        await AuthService.logout()
      } catch {
        // Server failure must not leave local identity active.
      } finally {
        this.logout()
      }
    },
    async cleanSession() {
      await this.logoutAction()
    },
    loginSuccess() {
      this.auth.status.loggedIn = true
    },
    logout() {
      this.auth.status.loggedIn = false
      this.userDetail = {}
      this.currentCollaborator = null
      localStorage.removeItem('userDetail')
      localStorage.removeItem('currentCollaborator')
    },
    updateCollaboratorsList(newCollaboratorList) {
      if (this.userDetail && this.userDetail.collaborators) {
        persistAuthCollaborators(
          this,
          newCollaboratorList
            .filter((collaborator) => !collaborator.status || collaborator.status === 'active')
            .map(toAuthCollaborator)
        )
      }
    },
    updateOneCollaboratorsInList(collaborator) {
      if (this.userDetail && this.userDetail.collaborators) {
        const existsAtIndex = this.userDetail.collaborators.findIndex(
          (element) => element.technical_id === collaborator.technical_id
        )
        if (existsAtIndex !== -1) {
          const collaborators = [...this.userDetail.collaborators]
          if (collaborator.status && collaborator.status !== 'active') {
            collaborators.splice(existsAtIndex, 1)
          } else {
            collaborators[existsAtIndex] = toAuthCollaborator(collaborator)
          }
          persistAuthCollaborators(this, collaborators)
        }
      }
    },
    updateProfile(profile) {
      this.userDetail.phone_number = profile.phone_number
      localStorage.setItem('userDetail', JSON.stringify(this.userDetail))
    },
    selectedCollaborator(newCurrentCollaboratorId) {
      if (this.userDetail && this.userDetail.collaborators) {
        const newCurrent = this.userDetail.collaborators.find(
          (item) => item.technical_id === newCurrentCollaboratorId
        )
        this.currentCollaborator = newCurrent
        localStorage.setItem('currentCollaborator', JSON.stringify(newCurrent))
      }
    },
    setRepairFileStoreSelected(store) {
      this.repairFile.StoreSelected = store
    },
    updateRepairFileListFilters(repairFileFilters) {
      this.filtersHistory.repairFileList = repairFileFilters
      localStorage.setItem('filtersHistory', JSON.stringify(this.filtersHistory))
    }
  }
})
